// ============================================
// OFFICEFORGED — Main Renderer Entry Point
// Boots card engine, editor, grid, sidebar, card panel
// ============================================

import { CardEngine } from './card-engine.js';
import { Editor } from './editor.js';
import { CardPanel } from './card-panel.js';
import { Sidebar } from './sidebar.js';
import { GridModule } from './grid-module.js';
import { DonutSelector } from './donut.js';
import { PDFViewer } from './pdf-viewer.js';
import { setupKeyboardShortcuts } from './keyboard.js';

document.addEventListener('DOMContentLoaded', () => {
  console.log('OfficeForged v0.2.0 — booting...');

  // 1. Card engine
  const cardEngine = new CardEngine();

  // 2. Card inspector panel
  const cardPanel = new CardPanel(cardEngine);

  // 3. ProseMirror editor (Docs)
  const editor = new Editor(cardEngine, cardPanel);

  // 4. Grid module (Spreadsheet)
  const grid = new GridModule(cardEngine);
  cardEngine._gridModule = grid;

  // 5. PDF Viewer
  const pdfViewer = new PDFViewer();

  // 6. Sidebar navigation
  const sidebar = new Sidebar(grid);

  // Wire cross-module references for Send to PDF
  editor._pdfViewer = pdfViewer;
  editor._sidebar = sidebar;

  // 6. Donut selector (Forged signature nav)
  const donut = new DonutSelector((moduleId) => {
    sidebar.switchModule(moduleId);
  });

  // 6. Status bar updates
  setupStatusBar(editor, cardEngine);

  // 7. Zoom control
  setupZoom();

  // 8. Find & Replace
  setupFind(editor);

  // 9. Line spacing
  setupLineSpacing();

  // 10. Keyboard shortcuts (standard Office/Google bindings)
  setupKeyboardShortcuts({ sidebar, editor, pdfViewer });

  // 11. Focus editor
  editor.focus();

  window._officeforged = { cardEngine, editor, cardPanel, sidebar, grid, donut, pdfViewer };
  console.log('OfficeForged ready.');
});

function setupStatusBar(editor, cardEngine) {
  const wordsEl = document.getElementById('status-words');
  const charsEl = document.getElementById('status-chars');
  const cardsEl = document.getElementById('status-cards');

  function update() {
    if (!editor.view) return;
    const text = editor.view.state.doc.textContent;
    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
    const chars = text.length;
    const cards = cardEngine.getAllCards().length;
    if (wordsEl) wordsEl.textContent = `${words} words`;
    if (charsEl) charsEl.textContent = `${chars} characters`;
    if (cardsEl) cardsEl.textContent = `${cards} cards`;
  }

  cardEngine.on(() => update());
  setInterval(update, 1000);
  update();
}

function setupZoom() {
  const sel = document.getElementById('sel-zoom');
  const editorPage = document.getElementById('editor-page');
  if (!sel || !editorPage) return;

  sel.addEventListener('change', () => {
    const zoom = parseInt(sel.value) / 100;
    editorPage.style.transform = `scale(${zoom})`;
    editorPage.style.transformOrigin = 'top center';
    editorPage.style.width = `${100 / zoom}%`;
  });
}

function setupFind(editor) {
  const btn = document.getElementById('btn-find');
  if (!btn) return;

  btn.addEventListener('click', () => {
    const term = prompt('Find text:');
    if (!term || !editor.view) return;

    const { doc } = editor.view.state;
    const text = doc.textContent;
    const index = text.toLowerCase().indexOf(term.toLowerCase());

    if (index >= 0) {
      // Find the position in the doc
      let pos = 0;
      let found = false;
      doc.descendants((node, nodePos) => {
        if (found || !node.isText) return;
        const nodeText = node.text || '';
        const localIndex = nodeText.toLowerCase().indexOf(term.toLowerCase());
        if (localIndex >= 0) {
          const from = nodePos + localIndex;
          const to = from + term.length;
          const tr = editor.view.state.tr.setSelection(
            require('prosemirror-state').TextSelection.create(editor.view.state.doc, from, to)
          );
          editor.view.dispatch(tr);
          editor.view.focus();
          found = true;
        }
      });

      if (!found) alert('Not found.');
    } else {
      alert('Not found.');
    }
  });
}

function setupLineSpacing() {
  const sel = document.getElementById('sel-line-spacing');
  const editorEl = document.getElementById('editor');
  if (!sel || !editorEl) return;

  sel.addEventListener('change', () => {
    const pm = editorEl.querySelector('.ProseMirror');
    if (pm) pm.style.lineHeight = sel.value;
  });
}
